import { codigoTotpSchema, type ConfiguracaoTotp } from '@mymaxx/shared';
import { Botao, CampoTexto } from '@mymaxx/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ErroApi, authApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

/**
 * Configuração do autenticador (TOTP). Obrigatória para Admin/Supervisor no primeiro acesso;
 * disponível para os demais perfis. Fluxo: gerar QR → escanear → digitar código → ativado.
 */
export function Configurar2fa() {
  const { usuario, definir } = useSessao();
  const navegar = useNavigate();
  const [cfg, setCfg] = useState<ConfiguracaoTotp | null>(null);
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Cada chamada gera um segredo novo; o StrictMode (dev) roda o efeito 2x — garantir uma só.
  const solicitado = useRef(false);

  useEffect(() => {
    if (!usuario || usuario.totpAtivo || solicitado.current) return;
    solicitado.current = true;
    authApi
      .configurarTotp()
      .then(setCfg)
      .catch((err) => setErro(err instanceof ErroApi ? err.mensagem : 'Falha ao gerar o QR code'));
  }, [usuario]);

  if (usuario?.totpAtivo) return <Navigate to="/" replace />;

  async function ativar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const parse = codigoTotpSchema.safeParse(codigo);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Código inválido');
      return;
    }
    setEnviando(true);
    try {
      definir(await authApi.ativarTotp(parse.data));
      navegar('/', { replace: true });
    } catch (err) {
      setErro(err instanceof ErroApi ? err.mensagem : 'Falha inesperada');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="mx-auto max-w-lg px-4 py-10">
      <img src="/logo.png" alt="MyMAXX" className="mx-auto size-14" width={56} height={56} />
      <h1 className="text-xl font-semibold">Ative o autenticador</h1>
      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
        {usuario?.totpPendente
          ? 'Seu perfil exige verificação em duas etapas. Isso leva um minuto.'
          : 'Proteja sua conta com um código que muda a cada 30 segundos.'}
      </p>

      <ol className="mt-6 space-y-6 text-sm">
        <li>
          <p className="font-medium">1. Instale um app autenticador</p>
          <p className="text-neutral-600 dark:text-neutral-400">
            Google Authenticator, Microsoft Authenticator, Authy ou 1Password.
          </p>
        </li>
        <li>
          <p className="font-medium">2. Escaneie o QR code</p>
          {cfg ? (
            <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <img
                src={cfg.qrCodeDataUrl}
                alt="QR code do autenticador"
                className="size-44 rounded-lg bg-white p-1"
              />
              <div className="text-xs text-neutral-600 dark:text-neutral-400">
                <p>Não consegue escanear? Digite esta chave no app:</p>
                <code className="mt-1 block break-all rounded bg-neutral-100 p-2 font-mono text-[11px] dark:bg-neutral-900">
                  {cfg.segredo}
                </code>
              </div>
            </div>
          ) : (
            <p className="text-neutral-500">{erro ?? 'Gerando QR code…'}</p>
          )}
        </li>
        <li>
          <p className="font-medium">3. Digite o código que o app mostra</p>
          <form className="mt-2 space-y-3" onSubmit={ativar} noValidate>
            <CampoTexto
              rotulo="Código de 6 dígitos"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={!cfg}
            />
            {erro && cfg && (
              <p role="alert" className="text-sm text-red-600">
                {erro}
              </p>
            )}
            <Botao type="submit" carregando={enviando} disabled={!cfg}>
              Ativar autenticador
            </Botao>
          </form>
        </li>
      </ol>
    </main>
  );
}
